import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ShieldCheck, Database, RefreshCw, Layers, CheckCircle2, AlertCircle } from 'lucide-react';
import { LatticeService } from '../services/latticeService';

export const UnifiedTruthVerification: React.FC = () => {
  const [audit, setAudit] = useState<any>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    fetchAudit();
  }, []);

  const fetchAudit = async () => {
    const data = await LatticeService.getUnifiedTruthAudit();
    setAudit(data);
  };

  const handleFullVerification = async () => {
    setIsVerifying(true);
    setProgress(0);
    
    // Simulate auditing different layers of the platform
    const layers = ["Tree Rings", "Ice Cores", "Carbon-14", "Lattice Logic", "Expansion Seed"];
    for (let i = 0; i <= 100; i += 10) {
      setProgress(i);
      await new Promise(resolve => setTimeout(resolve, 200));
    }
    
    await fetchAudit();
    setIsVerifying(false);
  };

  return (
    <div id="unified-truth-verification" className="w-full bg-[#111] border-b border-white/5 py-4 px-6 relative overflow-hidden group hover:bg-[#151515] transition-colors">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-6">
          <div className="relative">
            <ShieldCheck className={`w-8 h-8 ${isVerifying ? 'text-white/20' : 'text-lattice-cyan'}`} />
            {isVerifying && (
              <motion.div 
                className="absolute inset-0 border-2 border-lattice-cyan rounded-full"
                animate={{ rotate: 360, scale: [1, 1.2, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              />
            )}
          </div>
          
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-[10px] font-mono font-bold tracking-[0.2em] text-white uppercase italic">Unified Truth Verification</h3>
              <span className="px-1.5 py-0.5 bg-lattice-cyan/10 border border-lattice-cyan/20 rounded-full text-[8px] font-mono text-lattice-cyan font-bold">14350 BP ANCHOR</span>
              <span className="px-1.5 py-0.5 bg-white/5 border border-white/10 rounded-full text-[8px] font-mono text-white/40 font-bold uppercase tracking-tighter">Tree Ring Sync</span>
            </div>
            <p className="text-[10px] font-mono text-white/40 uppercase tracking-widest leading-none">
              Status: {isVerifying ? `Auditing Physical Records... ${progress}%` : audit?.status === "STABLE" ? "Doors Open: Physics Correlated" : "Verification Required"}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-8">
          <div className="hidden lg:flex items-center gap-12 text-[10px] font-mono text-white/30 uppercase tracking-widest">
            <div className="flex flex-col gap-1">
              <span className="text-[8px] text-white/20">Nodes Verified</span>
              <span className="text-white/60">{audit?.nodes_verified || "---"}</span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[8px] text-white/20">Drift Suppression</span>
              <span className="text-lattice-cyan flex items-center gap-2">
                ACTIVE <CheckCircle2 className="w-2.5 h-2.5" />
              </span>
            </div>
          </div>

          <button 
            onClick={handleFullVerification}
            disabled={isVerifying}
            className="flex items-center gap-3 bg-white/5 hover:bg-white/10 border border-white/10 px-6 py-2.5 transition-all group disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 text-lattice-cyan group-hover:rotate-180 transition-transform duration-700 ${isVerifying ? 'animate-spin' : ''}`} />
            <span className="text-[10px] font-mono font-bold text-white uppercase tracking-widest">Global Sync</span>
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isVerifying && (
          <motion.div 
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            exit={{ opacity: 0 }}
            className="absolute bottom-0 left-0 h-[2px] bg-lattice-cyan shadow-[0_0_10px_#00E5FF]"
          />
        )}
      </AnimatePresence>
    </div>
  );
};
